import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heart, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        // Continue polling
        setTimeout(() => {
          setAttempts(attempt + 1);
          pollPaymentStatus(sessionId, attempt + 1);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex items-center justify-center p-6">
      {/* Navbar */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-lg border-b border-gray-200 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Heart className="w-8 h-8 text-#8B4513" />
            <span className="text-2xl font-bold text-gray-900">Ourmend</span>
          </div>
        </div>
      </nav>

      <Card className="max-w-md w-full p-12 text-center mt-20" data-testid="payment-status-card">
        {status === 'checking' && (
          <div className="space-y-6">
            <Loader2 className="w-16 h-16 text-#8B4513 mx-auto animate-spin" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
              <p className="text-gray-600">Please wait while we confirm your payment</p>
              <p className="text-sm text-gray-500 mt-4">Attempt {attempts + 1} of {maxAttempts}</p>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6 fade-in" data-testid="success-message">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Payment Successful!</h2>
              <p className="text-lg text-gray-700 mb-2">Your booking has been confirmed.</p>
              <p className="text-gray-600">You will receive a confirmation email shortly.</p>
            </div>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-#8B4513 hover:bg-#6B3410 w-full h-12 text-base"
              data-testid="view-bookings-btn"
            >
              View My Bookings
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6" data-testid="error-message">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Payment Issue</h2>
              <p className="text-gray-700 mb-2">We couldn't confirm your payment.</p>
              <p className="text-gray-600">Please check your bookings or contact support.</p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-#8B4513 hover:bg-#6B3410 w-full"
                data-testid="go-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              <Button
                onClick={() => navigate('/browse')}
                variant="outline"
                className="w-full"
                data-testid="browse-again-btn"
              >
                Browse Healers
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PaymentSuccess;
